import { PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import { AxiosResponse } from 'axios';
import { put, takeLatest } from 'redux-saga/effects';
import { setAllocationFileRecordIds } from 'redux/slice/allocation/AllocationDetails.slice';
import {
  createBorrowerGroup,
  createBorrowerGroupFailure,
  createBorrowerGroupSuccess,
  fetchAgentsByCampaign,
  fetchAgentsByCampaignFailure,
  fetchAgentsByCampaignSuccess,
  fetchAgentsFailure,
  fetchAssignedAgents,
  fetchAssignedAgentsSuccess,
  fetchBorrowerGroupsBySupervisorId,
  fetchBorrowerGroupsBySupervisorIdFailure,
  fetchBorrowerGroupsBySupervisorIdSuccess,
  fetchBorrowerStats,
  fetchBorrowerStatsFailure,
  fetchBorrowerStatsSuccess,
  fetchCallPlannerDataGrid,
  fetchCallPlannerDataGridFailure,
  fetchCallPlannerDataGridHeaders,
  fetchCallPlannerDataGridHeadersFailure,
  fetchCallPlannerDataGridHeadersSuccess,
  fetchCallPlannerDataGridSuccess,
  fetchCallPlannerDataGridSummary,
  fetchCallPlannerDataGridSummaryFailure,
  fetchCallPlannerDataGridSummarySuccess,
  fetchCallPlannerMetrics,
  fetchCallPlannerMetricsFailure,
  fetchCallPlannerMetricsSuccess,
  fetchCampaignAllocationSummary,
  fetchCampaignAllocationSummaryFailure,
  fetchCampaignAllocationSummarySuccess,
  fetchCampaigns,
  fetchCampaignsFailure,
  fetchCampaignsSuccess,
  fetchOtherAgents,
  fetchOtherAgentsSuccess,
  getCallPlannerDataGridUniqueValuesByColumnName,
  getCallPlannerDataGridUniqueValuesByColumnNameFailure,
  getCallPlannerDataGridUniqueValuesByColumnNameSuccess,
  reassignCampaignBorrowerGroup,
  reassignCampaignBorrowerGroupFailure,
  reassignCampaignBorrowerGroupSuccess,
  saveLastSelectedGroup,
  searchInDataGrid,
  searchInFilterByValues,
  searchInFilterByValuesFailure,
  searchInFilterByValuesSuccess,
  setSelectedGroup,
  unassignCampaignBorrowerGroup,
  unassignCampaignBorrowerGroupFailure,
  unassignCampaignBorrowerGroupSuccess,
} from 'redux/slice/call-center/CallPlanner.slice';
import {
  setFooterSummary,
  setUniqueValues,
} from 'redux/slice/filter/Filter.slice';
import { IGetUniqueValuesByColumnResponse } from 'redux/types/Agent.types';
import {
  IBorrowerStatsRequest,
  IBorrowerStatsResponse,
  ICallPlannerMetricsRequest,
  ICallPlannerMetricsResponse,
  ICreateBorrowerGroupsRequest,
  ICreateBorrowerGroupsResponse,
  IFetchAgentsByCampaignRequest,
  IFetchAgentsByCampaignResponse,
  IFetchAgentsInAndOutOfCampaignRequest,
  IFetchAgentsInAndOutOfCampaignResponse,
  IFetchBorrowerGroupsRequest,
  IFetchBorrowerGroupsResponse,
  IFetchCampaignAllocationSummaryRequest,
  IFetchCampaignAllocationSummaryResponse,
  IFetchCampaignsRequest,
  IFetchCampaignsResponse,
  IGetCallDataGridHeadersRequest,
  IGetCallDataGridHeadersResponse,
  IGetCallDataGridRequest,
  IGetCallDataGridResponse,
  IGetCallDataGridSummaryRequest,
  IGetCallDataGridSummaryResponse,
  IGetCallUniqueValuesByColumnRequest,
  IGetCallUniqueValuesByColumnResponse,
  IGetSearchInDataGridRequest,
  IGetSearchInDataGridResponse,
  IReassignCampaignBorrowerGroupRequest,
  ISearchInFilterByValuesRequest,
} from 'redux/types/Call.types';
import { callPlannerEndPoints } from 'services/api/endPoints';
import { HTTPClient } from 'services/api/http-client';

import { handleError } from '../utils/errorUtils';

// Saga for fetching call planner data grid
function* fetchCallPlannerDataGridSaga(
  action: PayloadAction<IGetCallDataGridRequest>
) {
  try {
    const {
      allocationId,
      channel,
      ungrouped,
      groupId = '',
      isAllocated,
      columns,
      limit = 10,
      offset = 0,
    } = action.payload;
    const response: AxiosResponse<IGetCallDataGridResponse> =
      yield HTTPClient.PostRequest(
        `${callPlannerEndPoints.GET_CALL_PLANNER_DETAILS(
          allocationId,
          channel,
          ungrouped,
          groupId,
          isAllocated,
          limit,
          offset
        )}`,
        { columns }
      );
    yield put(fetchCallPlannerDataGridSuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, fetchCallPlannerDataGridFailure, '');
  }
}
// Saga for fetching allocation file headers
function* fetchCallPlannerDataGridHeadersSaga(
  action: PayloadAction<IGetCallDataGridHeadersRequest>
) {
  try {
    const { allocationId, channel } = action.payload;
    const response: AxiosResponse<IGetCallDataGridHeadersResponse> =
      yield HTTPClient.GetRequest(
        `${callPlannerEndPoints.GET_CALL_PLANNER_HEADERS(
          allocationId,
          channel
        )}`
      );
    yield put(fetchCallPlannerDataGridHeadersSuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, fetchCallPlannerDataGridHeadersFailure, '');
  }
}
// Saga for fetching allocation file summary
function* fetchCallPlannerDataGridSummarySaga(
  action: PayloadAction<IGetCallDataGridSummaryRequest>
) {
  try {
    const {
      columns,
      allocationId,
      groupId = '',
      channel,
      isAllocated = false,
      ungrouped = false,
      searchValue = '',
    } = action.payload;
    const response: AxiosResponse<IGetCallDataGridSummaryResponse> =
      yield HTTPClient.PostRequest(
        `${callPlannerEndPoints.GET_CALL_PLANNER_SUMMARY(
          allocationId,
          groupId,
          channel,
          isAllocated,
          ungrouped,
          searchValue
        )}`,
        { columns }
      );
    yield put(fetchCallPlannerDataGridSummarySuccess(response.data));
    yield put(setFooterSummary(response.data));
  } catch (error: any) {
    yield* handleError(error, fetchCallPlannerDataGridSummaryFailure, '');
  }
}
// Saga for getting unique values by column name
function* getCallPlannerDataGridUniqueValuesByColumnNameSaga(
  action: PayloadAction<IGetCallUniqueValuesByColumnRequest>
) {
  const {
    columnName,
    allocationId,
    channel,
    columns,
    groupId = '',
    isAllocated = false,
    ungrouped = false,
    limit = 10,
    offset = 0,
  } = action.payload;
  try {
    const response: AxiosResponse<IGetCallUniqueValuesByColumnResponse> =
      yield HTTPClient.PostRequest(
        `${callPlannerEndPoints.GET_UNIQUE_VALUES_BY_COLUMN(
          allocationId,
          channel,
          columnName,
          groupId,
          isAllocated,
          ungrouped,
          limit,
          offset
        )}`,
        { columns }
      );

    yield put(
      getCallPlannerDataGridUniqueValuesByColumnNameSuccess(response.data)
    );
    yield put(setUniqueValues(response.data));
    // Special handling for allocationRecordId
    if (columnName === 'id') {
      yield put(setAllocationFileRecordIds(response.data));
    }
    // message.success('Unique values fetched successfully');
  } catch (error: any) {
    yield* handleError(
      error,
      getCallPlannerDataGridUniqueValuesByColumnNameFailure,
      ''
    );
  }
}

// Saga for searching in filter by values
function* searchInFilterByValuesSaga(
  action: PayloadAction<ISearchInFilterByValuesRequest>
) {
  try {
    const {
      columnName,
      columnType,
      columnValue,
      allocationId,
      channel,
      groupId,
      isAllocated = false,
      ungrouped = false,
    } = action.payload;

    const baseUrl = `${callPlannerEndPoints.SEARCH_IN_FILTER_BY_VALUES(
      allocationId,
      isAllocated,
      ungrouped
    )}`;

    const params = new URLSearchParams({
      channel,
      columnName,
      columnType,
      columnValue,
      groupId,
    });

    const fullUrl = `${baseUrl}&${params.toString()}`;

    const requestBody = {
      columns: [],
    };

    const response: AxiosResponse<IGetUniqueValuesByColumnResponse> =
      yield HTTPClient.PostRequest(fullUrl, requestBody);

    yield put(searchInFilterByValuesSuccess(response.data));
    yield put(setUniqueValues(response.data));
  } catch (error: any) {
    yield* handleError(error, searchInFilterByValuesFailure, '');
  }
}

// Saga for searching in data grid
function* searchInDataGridSaga(
  action: PayloadAction<IGetSearchInDataGridRequest>
) {
  try {
    const {
      allocationId,
      channel,
      value,
      columns,
      groupId = '',
      isAllocated,
      ungrouped,
    } = action.payload;
    const response: AxiosResponse<IGetSearchInDataGridResponse> =
      yield HTTPClient.PostRequest(
        `${callPlannerEndPoints.SEARCH_IN_DATAGRID(
          allocationId,
          channel,
          value,
          groupId,
          isAllocated,
          ungrouped
        )}`,
        { columns }
      );
    yield put(fetchCallPlannerDataGridSuccess(response.data));
    // Call the fetchCallPlannerDataGridSummary with required parameters
    const fetchCallPlannerDataGridSummaryAction = {
      type: fetchCallPlannerDataGridSummary.type,
      payload: {
        columns,
        allocationId,
        groupId: '',
        channel,
        isAllocated,
        ungrouped,
        searchValue: value,
      },
    };
    yield put(fetchCallPlannerDataGridSummaryAction); // Dispatch the action
    message.success('Allocation records fetched successfully');
  } catch (error: any) {
    yield* handleError(error, fetchCallPlannerDataGridFailure, '');
  }
}

// Saga for fetching allocation file metrics
function* fetchCallPlannerMetricsSaga(
  action: PayloadAction<ICallPlannerMetricsRequest>
) {
  try {
    const { agencyId, allocationId, channel } = action.payload;
    const response: AxiosResponse<ICallPlannerMetricsResponse> =
      yield HTTPClient.GetRequest(
        `${callPlannerEndPoints.GET_CALL_FIELD_PLANNER_METRICS(
          agencyId,
          allocationId,
          channel
        )}`
      );
    yield put(fetchCallPlannerMetricsSuccess(response.data));
    // message.success('Call planner metrics fetched successfully');
  } catch (error: any) {
    yield* handleError(error, fetchCallPlannerMetricsFailure, '');
  }
}
// Saga for fetching campaigns
function* fetchCampaignsSaga(action: PayloadAction<IFetchCampaignsRequest>) {
  try {
    const { agencyId } = action.payload;
    const response: AxiosResponse<IFetchCampaignsResponse[]> =
      yield HTTPClient.GetRequest(
        `${callPlannerEndPoints.GET_CAMPAIGNS(agencyId)}`
      );
    yield put(fetchCampaignsSuccess(response.data));
    // message.success('Allocation file summary fetched successfully');
  } catch (error: any) {
    yield* handleError(error, fetchCampaignsFailure, '');
  }
}
// Saga for fetching agents in that campaign and other agents
function* fetchAssignedAgentsSaga(
  action: PayloadAction<IFetchAgentsInAndOutOfCampaignRequest>
) {
  try {
    const {
      campaignIds = [],
      supervisorId = '',
      allocationFileId,
    } = action.payload;

    const response: AxiosResponse<IFetchAgentsInAndOutOfCampaignResponse> =
      yield HTTPClient.PostRequest(
        `${callPlannerEndPoints.GET_AGENTS_IN_AND_OUT_OF_CAMPAIGN()}`,
        {
          assigned: true,
          campaign_ids: campaignIds,
          supervisor_id: supervisorId,
          allocation_file_id: allocationFileId,
        }
      );

    yield put(fetchAssignedAgentsSuccess(response.data.data));
  } catch (error: any) {
    yield* handleError(error, fetchAgentsFailure, '');
  }
}

function* fetchOtherAgentsSaga(
  action: PayloadAction<IFetchAgentsInAndOutOfCampaignRequest>
) {
  try {
    const {
      campaignIds = [],
      supervisorId = '',
      allocationFileId,
    } = action.payload;

    const response: AxiosResponse<IFetchAgentsInAndOutOfCampaignResponse> =
      yield HTTPClient.PostRequest(
        `${callPlannerEndPoints.GET_AGENTS_IN_AND_OUT_OF_CAMPAIGN()}`,
        {
          assigned: false,
          campaign_ids: campaignIds,
          supervisor_id: supervisorId,
          allocation_file_id: allocationFileId,
        }
      );

    yield put(fetchOtherAgentsSuccess(response.data.data));
  } catch (error: any) {
    yield* handleError(error, fetchAgentsFailure, '');
  }
}

// Saga for creating borrower group
function* createBorrowerGroupSaga(
  action: PayloadAction<ICreateBorrowerGroupsRequest>
) {
  try {
    const {
      columns,
      allocationId,
      campaignIds,
      name,
      selectedBorrowerIds,
      unselectedBorrowerIds,
      agentIds,
      supervisorId,
      ungrouped,
      isAllocated,
    } = action.payload;
    const response: AxiosResponse<ICreateBorrowerGroupsResponse> =
      yield HTTPClient.PostRequest(
        `${callPlannerEndPoints.CREATE_BORROWER_GROUP(ungrouped, isAllocated)}`,
        {
          columns,
          allocation_id: allocationId,
          campaign_ids: campaignIds,
          name,
          selectedBorrowerIds,
          unselectedBorrowerIds,
          agent_ids: agentIds,
          supervisor_id: supervisorId,
        }
      );
    yield put(createBorrowerGroupSuccess(response.data));
    // Call last selected group
    const saveLastSelectedGroupAction = {
      type: saveLastSelectedGroup.type,
      payload: {
        groupId: response.data.id,
      },
    };
    yield put(saveLastSelectedGroupAction); // Dispatch the action
    message.success(response.data.message);
  } catch (error: any) {
    yield* handleError(error, createBorrowerGroupFailure, '');
  }
}
// Saga for fetching borrower groups by supervisor
function* fetchBorrowerGroupsBySupervisorIdSaga(
  action: PayloadAction<IFetchBorrowerGroupsRequest>
) {
  try {
    const { allocationFileId, supervisorId = '' } = action.payload;
    const response: AxiosResponse<IFetchBorrowerGroupsResponse[]> =
      yield HTTPClient.GetRequest(
        `${callPlannerEndPoints.GET_BORROWER_GROUPS(
          allocationFileId,
          supervisorId
        )}`
      );
    yield put(fetchBorrowerGroupsBySupervisorIdSuccess(response.data));
    const lastSelectedGroup = response.data.filter(
      (group) => group.isGroupSelected === true
    );
    if (lastSelectedGroup.length <= 0) return;
    yield put(setSelectedGroup(lastSelectedGroup[0]));
    // message.success('Allocation file summary fetched successfully');
  } catch (error: any) {
    yield* handleError(error, fetchBorrowerGroupsBySupervisorIdFailure, '');
  }
}
// Saga for fetching campaign allocation summary
function* fetchCampaignAllocationSummarySaga(
  action: PayloadAction<IFetchCampaignAllocationSummaryRequest>
) {
  try {
    const { agencyId } = action.payload;
    const response: AxiosResponse<IFetchCampaignAllocationSummaryResponse> =
      yield HTTPClient.GetRequest(
        `${callPlannerEndPoints.GET_CAMPAIGN_ALLOCATION_SUMMARY(agencyId)}`
      );
    yield put(fetchCampaignAllocationSummarySuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, fetchCampaignAllocationSummaryFailure, '');
  }
}
// Saga for fetching all agents by campaign
function* fetchAgentsByCampaignSaga(
  action: PayloadAction<IFetchAgentsByCampaignRequest>
) {
  try {
    const { supervisorId, borrowerId } = action.payload;
    const response: AxiosResponse<IFetchAgentsByCampaignResponse[]> =
      yield HTTPClient.PostRequest(
        `${callPlannerEndPoints.FETCH_AGENTS_BY_CAMPAIGN()}`,
        { supervisor_id: supervisorId, borrower_id: borrowerId }
      );
    yield put(fetchAgentsByCampaignSuccess(response.data));
    // message.success('Call records fetched successfully');
  } catch (error: any) {
    yield* handleError(error, fetchAgentsByCampaignFailure, '');
  }
}
// Saga for unassigning campaign from borrower group
function* unassignCampaignBorrowerGroupSaga(
  action: PayloadAction<{ groupId: string; allocationId: string }>
) {
  try {
    const { groupId, allocationId } = action.payload;

    const response: AxiosResponse<{ message: string }> =
      yield HTTPClient.PostRequest(
        `${callPlannerEndPoints.UNASSIGN_CAMPAIGN_BORROWER_GROUP(
          groupId,
          allocationId
        )}`
      );

    yield put(unassignCampaignBorrowerGroupSuccess(response.data));
    message.success(response.data.message);

    // Call the fetchCallPlannerDataGrid with required parameters
    const fetchCallPlannerDataGridAction = {
      type: fetchCallPlannerDataGrid.type,
      payload: {
        allocationId,
        channel: 'CALL',
        ungrouped: false,
        isAllocated: true,
        // ...pagination,
        // columns: transformFiltersToPayload(appliedFilters),
        groupId,
      },
    };
    yield put(fetchCallPlannerDataGridAction); // Dispatch the action
  } catch (error: any) {
    yield* handleError(error, unassignCampaignBorrowerGroupFailure, '');
  }
}
// Saga for reassigning campaign from borrower group
function* reassignCampaignBorrowerGroupSaga(
  action: PayloadAction<IReassignCampaignBorrowerGroupRequest>
) {
  try {
    const { groupId, allocationId, campaignIds, agentIds, supervisorId } =
      action.payload;

    const response: AxiosResponse<{ message: string }> =
      yield HTTPClient.PostRequest(
        `${callPlannerEndPoints.REASSIGN_CAMPAIGN_BORROWER_GROUP(groupId)}`,
        {
          allocationId,
          campaignIds,
          agentIds,
          supervisorId,
        }
      );

    yield put(reassignCampaignBorrowerGroupSuccess(response.data));
    message.success(response.data.message);

    // Call the fetchCallPlannerDataGrid with required parameters
    const fetchCallPlannerDataGridAction = {
      type: fetchCallPlannerDataGrid.type,
      payload: {
        allocationId,
        channel: 'CALL',
        ungrouped: false,
        isAllocated: true,
        // ...pagination,
        // columns: transformFiltersToPayload(appliedFilters),
        groupId,
      },
    };
    yield put(fetchCallPlannerDataGridAction); // Dispatch the action
  } catch (error: any) {
    yield* handleError(error, reassignCampaignBorrowerGroupFailure, '');
  }
}

// Saga for fetching borrower stats for a campaign
function* fetchBorrowerStatsSaga(action: PayloadAction<IBorrowerStatsRequest>) {
  const { campaignId } = action.payload;
  try {
    const response: AxiosResponse<IBorrowerStatsResponse> =
      yield HTTPClient.GetRequest(
        `${callPlannerEndPoints.FETCH_BORROWER_STATS(campaignId)}`
      );
    yield put(fetchBorrowerStatsSuccess({ campaignId, data: response.data }));
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.message || 'Failed to fetch borrower stats';
    const errorDetails =
      error?.response?.data?.details || 'Currently we are facing some issue';
    yield put(fetchBorrowerStatsFailure({ error: errorMessage, campaignId }));
    message.error(`${errorMessage}: ${errorDetails}`);
  }
}

// Saga for saving last selected group
// function* saveLastSelectedGroupSaga(
//   action: PayloadAction<ISaveLastSelectedGroupRequest>
// ) {
//   try {
//     const { groupId } = action.payload;

//     const response: AxiosResponse<ISaveLastSelectedGroupResponse> =
//       yield HTTPClient.PostRequest(
//         `${callPlannerEndPoints.SAVE_LAST_SELECTED_GROUP()}`,
//         {
//           groupId,
//         }
//       );

//     yield put(saveLastSelectedGroupSuccess(response.data));
//   } catch (error: any) {
//     yield* handleError(error, saveLastSelectedGroupFailure, '');
//   }
// }

export function* watchCallPlannerSagas() {
  yield takeLatest(fetchCallPlannerDataGrid.type, fetchCallPlannerDataGridSaga);
  yield takeLatest(
    fetchCallPlannerDataGridHeaders.type,
    fetchCallPlannerDataGridHeadersSaga
  );
  yield takeLatest(
    fetchCallPlannerDataGridSummary.type,
    fetchCallPlannerDataGridSummarySaga
  );
  yield takeLatest(
    getCallPlannerDataGridUniqueValuesByColumnName.type,
    getCallPlannerDataGridUniqueValuesByColumnNameSaga
  );
  yield takeLatest(searchInFilterByValues.type, searchInFilterByValuesSaga);
  yield takeLatest(searchInDataGrid.type, searchInDataGridSaga);
  yield takeLatest(fetchCallPlannerMetrics.type, fetchCallPlannerMetricsSaga);
  yield takeLatest(fetchCampaigns.type, fetchCampaignsSaga);
  yield takeLatest(
    fetchBorrowerGroupsBySupervisorId.type,
    fetchBorrowerGroupsBySupervisorIdSaga
  );
  // yield takeLatest(fetchBorrowersInGroup.type, fetchBorrowersInGroupSaga);
  yield takeLatest(
    fetchCampaignAllocationSummary.type,
    fetchCampaignAllocationSummarySaga
  );
  yield takeLatest(createBorrowerGroup.type, createBorrowerGroupSaga);
  yield takeLatest(fetchAgentsByCampaign.type, fetchAgentsByCampaignSaga);
  yield takeLatest(fetchAssignedAgents.type, fetchAssignedAgentsSaga);
  yield takeLatest(fetchOtherAgents.type, fetchOtherAgentsSaga);
  yield takeLatest(
    unassignCampaignBorrowerGroup.type,
    unassignCampaignBorrowerGroupSaga
  );
  yield takeLatest(
    reassignCampaignBorrowerGroup.type,
    reassignCampaignBorrowerGroupSaga
  );
  yield takeLatest(fetchBorrowerStats.type, fetchBorrowerStatsSaga);
  // yield takeLatest(saveLastSelectedGroup.type, saveLastSelectedGroupSaga);
}
